import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { Folder, UserPlus } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { XStack } from "tamagui";
import { useApi } from "../core/apis";
import { useStores } from "../core/stores";
import { Body } from "../ui/Body";
import { Box } from "../ui/Box";
import { Group } from "../ui/Group";
import { Label } from "../ui/Label";
import { ModuleIcon } from "../ui/ModuleIcon";
import { Screen } from "../ui/Screen";
import { TipsText } from "../ui/TipsText";
import { usePlanNavigation } from "./usePlanNavigation";

type Props = NativeStackScreenProps<ReactNavigation.RootParamList, "Archive">;

export const ArchiveScreen = observer(({ route }: Props) => {
  const navigation = usePlanNavigation();
  const planUid = route.params.planUid;

  const { plansApi, modulesApi, sectionsApi, usersApi } = useApi();
  const { plansStore, modulesStore, sectionsStore, usersStore } = useStores();

  const plan = plansStore.byId.get(planUid);

  const [isLoaded, setIsLoaded] = React.useState(false);

  React.useEffect(() => {
    async function loadData() {
      if (!plan) {
        await plansApi.getById(planUid);
      } else if (!isLoaded) {
        setIsLoaded(true);
        plan.data.archive.moduleIds.forEach((uid) => {
          modulesApi.getById(uid);
        });
        plan.data.archive.sectionIds.forEach((uid) => {
          sectionsApi.getById(uid);
        });
        plan.data.archive.userIds.forEach((uid) => {
          usersApi.getById(uid);
        });
      }
    }

    void loadData();
  }, [plan, isLoaded, plansApi, usersApi, sectionsApi, modulesApi, planUid]);

  if (!plan) {
    return null;
  }

  const archive = plan.data.archive;

  return (
    <Screen>
      <Box>
        <Label>Restore your groups and cards</Label>
        <Body>Any already archived item can be restored</Body>
      </Box>

      <Box>
        <Label>Archived Cards</Label>
        <TipsText text="Visit cards to restore them" />

        {!archive.moduleIds.length && <Body>None found</Body>}
        <Group>
          {archive.moduleIds.map((moduleUid) => {
            const m = modulesStore.byId.get(moduleUid);

            if (!m) {
              return null;
            }

            return (
              <XStack
                key={moduleUid}
                width={"100%"}
                alignItems="center"
                gap="$space.small"
                onPress={() => {
                  navigation.navigate("Module", {
                    planUid: planUid,
                    moduleUid: moduleUid,
                  });
                }}
                cursor="pointer"
              >
                <ModuleIcon module={m} />
                <Body>{m.data.title}</Body>
              </XStack>
            );
          })}
        </Group>
      </Box>

      <Box>
        <Label>Archived Sections</Label>
        {!archive.sectionIds.length && <Body>None found</Body>}
        <Group>
          {archive.sectionIds.map((uid) => {
            const s = sectionsStore.byId.get(uid);

            if (!s) {
              return null;
            }

            return (
              <XStack
                key={uid}
                width={"100%"}
                alignItems="center"
                gap="$space.small"
                onPress={() => {
                  plan.unArchiveSection(uid);
                }}
                cursor="pointer"
              >
                <Folder />
                <Body>Restore "{s.data.title}"</Body>
              </XStack>
            );
          })}
        </Group>
      </Box>

      <Box>
        <Label>Archived Users</Label>
        {!archive.userIds.length && <Body>None found</Body>}
        <Group>
          {archive.userIds.map((uid) => {
            const u = usersStore.byId.get(uid);

            if (!u) {
              return null;
            }

            return (
              <XStack
                key={uid}
                width={"100%"}
                alignItems="center"
                gap="$space.small"
                onPress={() => {
                  plan.unArchiveUser(uid);
                }}
                cursor="pointer"
              >
                <UserPlus />
                <Body>Restore "{u.data.displayName}"</Body>
              </XStack>
            );
          })}
        </Group>
      </Box>
    </Screen>
  );
});

export default ArchiveScreen;
